import { Controller } from 'stimulus'

/**
 * Manages grant tagging for GL entries.
 * Allows users to tag/untag GL entries to grant awards via AJAX.
 *
 * Usage:
 *   <div data-controller="grant-tag"
 *        data-grant-tag-entry-id="123"
 *        data-grant-tag-current-award-id=""
 *        data-grant-tag-current-tagged-entry-id="">
 *     <select data-target="grant-tag.awardSelect" data-action="change->grant-tag#tag">
 *       <option value="">— None —</option>
 *       <option value="1">FHWA-2026</option>
 *     </select>
 *     <select data-target="grant-tag.designationSelect" class="hidden">
 *       <option value="">—</option>
 *       <option value="award">Award</option>
 *       <option value="match">Match</option>
 *     </select>
 *     <span data-target="grant-tag.status"></span>
 *   </div>
 */
export default class extends Controller {
  static get targets() {
    return ['awardSelect', 'designationSelect', 'status']
  }

  get entryId() {
    return this.data.get('entryId')
  }

  get currentAwardId() {
    return this.data.get('currentAwardId')
  }

  set currentAwardId(value) {
    this.data.set('currentAwardId', value)
  }

  get currentTaggedEntryId() {
    return this.data.get('currentTaggedEntryId')
  }

  set currentTaggedEntryId(value) {
    this.data.set('currentTaggedEntryId', value)
  }

  connect() {
    this.inflight = null
    this.optionsPopulated = false
    this.pendingSwitch = false
    this.awardSelectTarget.addEventListener('focus', this.populateOptions.bind(this), { once: true })
    this.updateDesignationVisibility()
  }

  populateOptions() {
    if (this.optionsPopulated) return
    this.optionsPopulated = true

    const template = document.getElementById('grant-award-options')
    if (!template) return

    const currentValue = this.awardSelectTarget.value
    this.awardSelectTarget.innerHTML = template.innerHTML
    if (currentValue) {
      this.awardSelectTarget.value = currentValue
    }
  }

  tag() {
    this.populateOptions()
    const awardId = this.awardSelectTarget.value
    const previousAwardId = this.currentAwardId

    if (awardId === '') {
      if (this.pendingSwitch) {
        // Cancel pending switch — restore the original tag selection
        this.pendingSwitch = false
        this.awardSelectTarget.value = previousAwardId || ''
        this.updateDesignationVisibility()
        this.setStatus('')
        return
      }
      if (previousAwardId && this.currentTaggedEntryId) {
        this.removeTag(previousAwardId, this.currentTaggedEntryId)
      } else {
        this.updateDesignationVisibility()
        this.setStatus('')
      }
      return
    }

    this.updateDesignationVisibility()

    if (this.needsDesignation()) {
      this.pendingSwitch = !!previousAwardId
      this.setStatus('Select designation')
      return
    }

    this.pendingSwitch = false

    if (previousAwardId && this.currentTaggedEntryId && previousAwardId !== awardId) {
      this.switchTag(previousAwardId, this.currentTaggedEntryId, awardId)
    } else if (!previousAwardId) {
      this.createTag(awardId)
    }
  }

  needsDesignation() {
    if (!this.hasDesignationSelectTarget) return false
    const option = this.awardSelectTarget.selectedOptions[0]
    const requiresMatch = option && option.dataset.requiresMatch === 'true'
    const majorType = this.data.get('majorType')
    return requiresMatch && majorType === 'Revenue' && !this.designationSelectTarget.value
  }

  updateDesignation() {
    const awardId = this.awardSelectTarget.value
    if (!awardId) return

    this.pendingSwitch = false

    if (this.currentAwardId && this.currentTaggedEntryId) {
      this.switchTag(this.currentAwardId, this.currentTaggedEntryId, awardId)
    } else {
      this.createTag(awardId)
    }
  }

  switchTag(oldAwardId, oldTaggedEntryId, newAwardId) {
    const designation = this.hasDesignationSelectTarget
      ? this.designationSelectTarget.value
      : ''
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    this.abortInflight()
    const controller = new AbortController()
    this.inflight = controller

    this.setStatus('Saving...')

    fetch(`/grant/awards/${newAwardId}/tagged_entries/${oldTaggedEntryId}`, {
      method: 'PATCH',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        grant_tagged_entry: {
          gl_entry_id: this.entryId,
          designation: designation || null
        }
      })
    })
      .then(response => {
        if (response.ok) return response.json()
        return response.json().then(data => { throw data })
      })
      .then(data => {
        this.currentAwardId = newAwardId
        this.currentTaggedEntryId = data.id
        this.setStatus('Tagged')
        this.updateDesignationVisibility()
      })
      .catch(data => {
        if (data.name === 'AbortError') return
        const msg = data.errors ? data.errors.join(', ') : 'Error switching tag'
        this.setStatus(msg)
        this.awardSelectTarget.value = this.currentAwardId || ''
        this.updateDesignationVisibility()
      })
      .finally(() => { this.inflight = null })
  }

  createTag(awardId, onSuccess, onError) {
    const designation = this.hasDesignationSelectTarget
      ? this.designationSelectTarget.value
      : ''
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    this.abortInflight()
    const controller = new AbortController()
    this.inflight = controller

    this.setStatus('Saving...')

    fetch(`/grant/awards/${awardId}/tagged_entries`, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        grant_tagged_entry: {
          gl_entry_id: this.entryId,
          designation: designation || null
        }
      })
    })
      .then(response => {
        if (response.ok) return response.json()
        return response.json().then(data => { throw data })
      })
      .then(data => {
        this.currentAwardId = awardId
        this.currentTaggedEntryId = data.id
        this.setStatus('Tagged')
        this.updateDesignationVisibility()
        if (onSuccess) onSuccess()
      })
      .catch(data => {
        if (data.name === 'AbortError') return
        const msg = data.errors ? data.errors.join(', ') : 'Error tagging entry'
        this.setStatus(msg)
        this.awardSelectTarget.value = this.currentAwardId || ''
        this.updateDesignationVisibility()
        if (onError) onError()
      })
      .finally(() => { this.inflight = null })
  }

  removeTag(awardId, taggedEntryId, callback) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    this.abortInflight()
    const controller = new AbortController()
    this.inflight = controller

    this.setStatus('Removing...')

    fetch(`/grant/awards/${awardId}/tagged_entries/${taggedEntryId}`, {
      method: 'DELETE',
      signal: controller.signal,
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      }
    })
      .then(response => {
        if (response.ok) {
          this.currentAwardId = ''
          this.currentTaggedEntryId = ''
          this.setStatus('')
          this.updateDesignationVisibility()
          if (callback) callback()
        } else {
          this.setStatus('Error removing tag')
          this.awardSelectTarget.value = this.currentAwardId || ''
          this.updateDesignationVisibility()
        }
      })
      .catch(data => {
        if (data && data.name === 'AbortError') return
        this.setStatus('Error removing tag')
        this.awardSelectTarget.value = this.currentAwardId || ''
        this.updateDesignationVisibility()
      })
      .finally(() => { this.inflight = null })
  }

  abortInflight() {
    if (this.inflight) {
      this.inflight.abort()
      this.inflight = null
    }
  }

  updateDesignationVisibility() {
    if (!this.hasDesignationSelectTarget) return

    const awardId = this.awardSelectTarget.value
    if (awardId) {
      const option = this.awardSelectTarget.selectedOptions[0]
      const requiresMatch = option && option.dataset.requiresMatch === 'true'
      const majorType = this.data.get('majorType')

      if (requiresMatch && majorType === 'Revenue') {
        this.designationSelectTarget.classList.remove('hidden')
      } else {
        this.designationSelectTarget.classList.add('hidden')
        this.designationSelectTarget.value = ''
      }
    } else {
      this.designationSelectTarget.classList.add('hidden')
      this.designationSelectTarget.value = ''
    }
  }

  setStatus(text) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = text
    }
  }
}
